import {ImportModuleColumnProfileColumn} from '../../../core/definitions/import-module';
import {BlankValueSuggestion} from '../blank-values-step/blank-values.model';
import {ColumnUsage} from '../column-usages';

/**
 * The Feltkoblinger tab's source column list: each column with where the
 * mapping reads it, its column profile and the blank-value suggestions
 * still open for it. The profile's numbers are raw counts, so a value
 * read as blank is marked rather than left out.
 */

/** Which columns the list shows: all, those the mapping reads nowhere,
 * or those it reads. */
export type SourceColumnFilter = 'all' | 'unused' | 'used';

/** One place the run reads a column, as the list names it: by `label`,
 * or by the translation key `labelKey`. */
export interface SourceColumnTarget {
  label: string;
  labelKey?: string;
  // The name of the lookup the field gets its value through.
  lookup: string | null;
}

/** The target the media join column stands for. */
export const MEDIA_JOIN_TARGET: SourceColumnTarget = {
  label: '', labelKey: 'TRANS__IMPORT__MAPPING__COLUMN_MEDIA_JOIN', lookup: null,
};

/** One of a column's most common values, and whether it is read as
 * blank. */
export interface SourceColumnValue {
  value: string;
  count: number;
  readAsBlank: boolean;
}

export interface SourceColumnEntry {
  name: string;
  // The run reads the column.
  used: boolean;
  // Each place the run reads the column.
  targets: SourceColumnTarget[];
  // The same, with the fields of one card read directly merged into one
  // target: "Historikkhendelse 1 · Dato fra, Dato til".
  summaryTargets: SourceColumnTarget[];
  // `null` until the column profile is in.
  profile: ImportModuleColumnProfileColumn | null;
  // The profile counts no filled cell.
  blank: boolean;
  // "LOFT (354), MUSHALL (2)" from the profile, else the sample values.
  valuesText: string;
  topValues: SourceColumnValue[];
  // The source's sample values, for the list to show before the profile.
  samples: string[];
  suggestions: BlankValueSuggestion[];
}

export interface SourceColumnListSource {
  columns: readonly {name: string; sample_values: string[]}[];
  profile: ReadonlyMap<string, ImportModuleColumnProfileColumn> | null;
  usages: ReadonlyMap<string, readonly ColumnUsage[]>;
  // The columns the run reads.
  used: ReadonlySet<string>;
  mediaJoinColumn: string | null;
  suggestions: readonly BlankValueSuggestion[];
  // The name of the lookup with key `key`.
  lookupName: (key: string) => string | null;
  isReadAsBlank: (column: string, value: string) => boolean;
}

/** The list's columns, in table order. */
export function sourceColumnEntries(source: SourceColumnListSource): SourceColumnEntry[] {
  return source.columns.map(column => {
    const profile = source.profile?.get(column.name) ?? null;
    const top = profile?.top ?? [];
    const usages = source.usages.get(column.name) ?? [];
    const mediaJoin = column.name === source.mediaJoinColumn ? [MEDIA_JOIN_TARGET] : [];
    return {
      name: column.name,
      used: source.used.has(column.name),
      targets: [
        ...usages.map(usage => ({
          label: usage.label,
          lookup: usage.lookup ? source.lookupName(usage.lookup) : null,
        })),
        ...mediaJoin,
      ],
      summaryTargets: [...mergedTargets(usages, source.lookupName), ...mediaJoin],
      profile,
      blank: profile?.filled === 0,
      valuesText: profile
        ? top.map(counted => `${counted.value} (${counted.count})`).join(', ')
        : column.sample_values.join(', '),
      topValues: top.map(counted => ({
        ...counted, readAsBlank: source.isReadAsBlank(column.name, counted.value),
      })),
      samples: column.sample_values,
      suggestions: source.suggestions.filter(suggestion => suggestion.column === column.name),
    };
  });
}

/** The card a usage's field is on, from its label "<card> · <field>";
 * `null` for a flat row. */
function usageCard(usage: ColumnUsage): string | null {
  const suffix = ` · ${usage.field}`;
  return usage.label !== usage.field && usage.label.endsWith(suffix)
    ? usage.label.slice(0, -suffix.length) : null;
}

/** `usages` as targets, the fields of one card read directly merged into
 * the card's first target. A usage through a lookup stays on its own, so
 * its lookup names one field. */
function mergedTargets(
  usages: readonly ColumnUsage[], lookupName: (key: string) => string | null,
): SourceColumnTarget[] {
  const targets: SourceColumnTarget[] = [];
  const cards = new Map<string, {target: SourceColumnTarget; fields: string[]}>();
  for (const usage of usages) {
    const card = usage.lookup ? null : usageCard(usage);
    const merged = card === null ? undefined : cards.get(card);
    if (merged) {
      merged.fields.push(usage.field);
      merged.target.label = `${card} · ${merged.fields.join(', ')}`;
      continue;
    }
    const target = {label: usage.label, lookup: usage.lookup ? lookupName(usage.lookup) : null};
    if (card !== null) {
      cards.set(card, {target, fields: [usage.field]});
    }
    targets.push(target);
  }
  return targets;
}

/** Whether `filter` shows `entry`, and its name holds `query`, in any
 * case. */
export function showsColumn(filter: SourceColumnFilter, query: string, entry: SourceColumnEntry): boolean {
  return (filter === 'all' || (filter === 'used') === entry.used)
    && entry.name.toLowerCase().includes(query.trim().toLowerCase());
}

/** A column profile's distinct count as the list shows it: "200+" where
 * the profile stopped counting. */
export function distinctText(profile: ImportModuleColumnProfileColumn): string {
  return profile.distinct_capped ? `${profile.distinct}+` : `${profile.distinct}`;
}
